import React, { useEffect, useRef } from "react";
import { Card, Radio, Button, Divider } from "antd";
import "./style.scss";
import { FormattedMessage, useIntl } from "react-intl";
import { AmexCardSvg, MaestroCardSvg, MasterCardSvg, VisaCardSvg } from "@assets";
import { AmazonPaySvg, ApplePaySvg, GooglePaySvg, LinkPaySvg, PaypalSvg } from "../../../../assets/icons/payment-select";
import { FunctionalRootState, getAccountPaymentButtonKeys } from "@share/utils";
import { useSelector } from "react-redux";
import { ILoginState } from "@share/store/slices";
import { isEmpty } from "lodash";

interface IProps {
  multiplePayments: any;
  setPaymentSelected: (payment: string) => void;
  scrollToPaymentSelect: boolean;
  scrollToPaymentSelectDisabled: () => void;
}

const PaymentMethodsSelect: React.FC<IProps> = ({ multiplePayments, setPaymentSelected, scrollToPaymentSelect, scrollToPaymentSelectDisabled }) => {
  const [selectedMethod, setSelectedMethod] = React.useState<string>('');
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const paymentButtonKeys = getAccountPaymentButtonKeys(account);
  const PAYMENT_BUTTON_IS_EMPTY = isEmpty(paymentButtonKeys);
  const intl = useIntl();
  const handleChange = (e: any) => {
    setSelectedMethod(e.target.value);
  };

  const handleCollapse = () => {
    setPaymentSelected(selectedMethod);
    scrollToPaymentSelectDisabled();
    animateHeightChange(() => setIsCollapsed(true));
  };

  const handleExpand = () => {
    animateHeightChange(() => setIsCollapsed(false));
  };

  const animateHeightChange = (callback: () => void) => {
    const container = containerRef.current;
    if (!container) return;

    const startHeight = container.offsetHeight;
    callback();
    requestAnimationFrame(() => {
      const endHeight = container.offsetHeight;
      container.style.height = `${startHeight}px`;
      requestAnimationFrame(() => {
        container.style.transition = "height 0.3s ease-in-out";
        container.style.height = `${endHeight}px`;
        container.addEventListener(
          "transitionend",
          () => {
            container.style.height = "";
            container.style.transition = "";
          },
          { once: true }
        );
      });
    });
  };

  const renderOption = (value: string, label: string, Icon?: React.FC) => (
    <div className="payment-option">
      <Radio value={value} className="payment-methods-select__card-flex">
        <span className="option-text">{label}</span>
      </Radio>
      {Icon && <Icon />}
    </div>
  );

  const paymentOptions = [
    {
      value: "card",
      label: <FormattedMessage id="debit.credit.card" />,
      Icon: () => (
        <div className="payment-method-select__cards" style={{ height: '60px', paddingTop: '15px' }}>
          <MaestroCardSvg />
          <MasterCardSvg />
          <VisaCardSvg />
          <AmexCardSvg />
        </div>
      ),
    },
    multiplePayments.googlePay && !PAYMENT_BUTTON_IS_EMPTY && paymentButtonKeys.includes('google_pay') && {
      value: "google_pay",
      label: "Google Pay",
      Icon: GooglePaySvg,
    },
    multiplePayments.applePay && !PAYMENT_BUTTON_IS_EMPTY && paymentButtonKeys.includes('apple_pay') && {
      value: "apple_pay",
      label: "Apple Pay",
      Icon: ApplePaySvg,
    },
    multiplePayments.link && !PAYMENT_BUTTON_IS_EMPTY && paymentButtonKeys.includes('link') && {
      value: "link",
      label: "Link",
      Icon: LinkPaySvg,
    },
    multiplePayments.paypal && !PAYMENT_BUTTON_IS_EMPTY && paymentButtonKeys.includes('paypal') && {
      value: "paypal",
      label: "PayPal",
      Icon: PaypalSvg,
    },
    multiplePayments.amazonPay && !PAYMENT_BUTTON_IS_EMPTY && paymentButtonKeys.includes('amazon_pay') && {
      value: "amazon_pay",
      label: "Amazon Pay",
      Icon: AmazonPaySvg,
    },
  ].filter(Boolean);

  const displayedOptions = isCollapsed
    ? paymentOptions.filter((option) => option.value === selectedMethod)
    : paymentOptions;

  useEffect(() => {
    if (PAYMENT_BUTTON_IS_EMPTY) {
      // No wallet picker configured for this account — auto-select Card so the parent renders the
      // card form on its own. The picker itself is hidden via the early return below.
      setSelectedMethod("card");
      setTimeout(() => {
        setPaymentSelected("card");
      }, 1);
    } else {
      // If button preferences exist, clear the selection and expand to show all options
      setSelectedMethod("");
      setPaymentSelected("");
      if (isCollapsed) {
        setIsCollapsed(false);
      }
    }
  }, [PAYMENT_BUTTON_IS_EMPTY]);

  // Hide the picker entirely when the account has no `payment_buttons` preference — the parent
  // already renders the card form once `paymentSelected === 'card'` (set in the effect above).
  if (PAYMENT_BUTTON_IS_EMPTY) {
    return null;
  }

  return (
    <Card
      className={`payment-methods-select__card mb-3 ${scrollToPaymentSelect ? "error-border" : ""}`}
      id="payment-methods-select__card"
    >
      <p className="payment-subtitle">{intl.formatMessage({id: 'select.payment.to.pay'})}</p>

      <div ref={containerRef}>
        <Radio.Group
          onChange={handleChange}
          value={selectedMethod}
          className={`payment-methods-select ${isCollapsed ? "collapsed" : ""}`}
        >
          {displayedOptions.map((option) => (
            <div key={option.value} className="payment-option-wrapper">
              {renderOption(option.value, option.label, option.Icon)}
              {!isCollapsed && <Divider className="payment-divider" />}
            </div>
          ))}
        </Radio.Group>
      </div>

      {isCollapsed && !PAYMENT_BUTTON_IS_EMPTY && (
        <Button
          type="default"
          block
          style={{ marginTop: 20 }}
          onClick={handleExpand}
        >
          {intl.formatMessage({id: 'select.payment.to.change'})}
        </Button>
      )}

      {scrollToPaymentSelect && (
        <p className="error-message">{intl.formatMessage({id: 'select.payment.to.method'})}</p>
      )}

      {(!isCollapsed || PAYMENT_BUTTON_IS_EMPTY) && (
        <Button
          type="primary"
          block
          style={{ marginTop: 20 }}
          disabled={!selectedMethod || PAYMENT_BUTTON_IS_EMPTY}
          onClick={handleCollapse}
        >
          {intl.formatMessage({id: 'select.payment.continue.to.pay'})}
          
        </Button>
      )}
    </Card>
  );
};

export default PaymentMethodsSelect;
